/** Projects type mirrors — apps/api/src/projects/{hub,commands}.controller.ts. */

export const PROJECT_STATUS_OPTIONS = [
  { value: 'planning', label: 'برنامه‌ریزی' },
  { value: 'active', label: 'فعال' },
  { value: 'on_hold', label: 'متوقف' },
  { value: 'completed', label: 'تکمیل‌شده' },
  { value: 'cancelled', label: 'لغو شده' },
] as const;

export const TASK_STATUS_OPTIONS = [
  { value: 'todo', label: 'انجام نشده' },
  { value: 'in_progress', label: 'در حال انجام' },
  { value: 'done', label: 'انجام‌شده' },
  { value: 'cancelled', label: 'لغو شده' },
] as const;

export const TASK_PRIORITY_OPTIONS = [
  { value: 'low', label: 'کم' },
  { value: 'medium', label: 'متوسط' },
  { value: 'high', label: 'بالا' },
  { value: 'urgent', label: 'فوری' },
] as const;

export const TASK_RELATED_TYPE_OPTIONS = [
  { value: 'party', label: 'مشتری / طرف حساب' },
  { value: 'sales_order', label: 'سفارش فروش' },
  { value: 'purchase_order', label: 'سفارش خرید' },
  { value: 'invoice', label: 'فاکتور مالی' },
  { value: 'lead', label: 'سرنخ فروش' },
] as const;

export type TaskRelatedType = (typeof TASK_RELATED_TYPE_OPTIONS)[number]['value'];

export interface TaskChecklistItem {
  readonly itemId: string;
  readonly title: string;
  readonly isDone: boolean;
  readonly sortOrder: number;
  readonly completedAt: string | null;
  readonly completedBy: string | null;
  readonly createdAt: string;
}

export interface TaskTimeEntry {
  readonly entryId: string;
  readonly accountId: string;
  readonly spentDate: string;
  readonly durationMinutes: number;
  readonly description: string | null;
  readonly billable: boolean;
  readonly createdAt: string;
}

export const REMINDER_CHANNEL_OPTIONS = [
  { value: 'in_app', label: 'داخل پنل' },
  { value: 'email', label: 'ایمیل' },
  { value: 'sms', label: 'پیامک' },
] as const;

export interface Project {
  readonly ref: string;
  readonly name: string;
  readonly statusLabel: string;
  readonly dueDate: string | null;
}

export interface ProjectTaskSummary {
  readonly total: number;
  readonly byStatus: readonly {
    readonly statusLabel: string;
    readonly count: number;
  }[];
}

export interface ProjectDetail {
  readonly ref: string;
  readonly name: string;
  readonly statusLabel: string;
  readonly dueDate: string | null;
  readonly isOverdue: boolean;
  readonly daysOverdue: number | null;
  readonly taskSummary: ProjectTaskSummary;
  readonly tasks: readonly ProjectTask[];
  readonly comments: readonly ProjectComment[];
}

export interface ProjectListResponse {
  readonly projects: readonly Project[];
  /** Paginated envelope (apps/api/src/projects/presentation.ts `paginatedEnvelope`). */
  readonly total?: number;
  readonly offset?: number;
  readonly limit?: number;
}

export interface OverdueProject extends Project {
  readonly daysOverdue: number;
}

export interface OverdueProjectListResponse {
  readonly projects: readonly OverdueProject[];
}

export interface TaskAssignee {
  readonly memberRef: string;
  readonly displayLabel: string;
}

export interface ProjectTask {
  readonly ref: string;
  readonly title: string;
  /** null for a standalone task (created without a project). */
  readonly projectRef: string | null;
  readonly statusLabel: string;
  readonly priorityLabel: string;
  readonly dueDate: string | null;
  readonly assignee: TaskAssignee | null;
  readonly relatedType?: TaskRelatedType | null;
  readonly relatedTypeLabel?: string | null;
  readonly relatedId?: string | null;
  readonly relatedRef?: string | null;
  readonly checklistSummary?: { readonly total: number; readonly done: number };
  readonly checklist?: readonly TaskChecklistItem[];
  readonly timeEntries?: readonly TaskTimeEntry[];
  readonly totalSpentMinutes?: number;
}

/** Label shown wherever a task's project would be, for a standalone task. */
export const NO_PROJECT_LABEL = 'بدون پروژه';

/**
 * `GET .../projects/assignable-members[?projectRef=]` and
 * `GET .../projects/:ref/members` — apps/api/src/projects/hub.controller.ts.
 * With a projectRef the list is that project's team only; without one it is
 * every active tenant member (a standalone task's assignee pool).
 */
export interface AssignableMember {
  readonly memberRef: string;
  readonly displayLabel: string;
  readonly isSelf: boolean;
  readonly isOwner: boolean;
}

export interface AssignableMembersResponse {
  readonly projectRef: string | null;
  readonly members: readonly AssignableMember[];
}

export interface ProjectMembersResponse {
  readonly projectRef: string;
  readonly canManage: boolean;
  readonly members: readonly AssignableMember[];
  /** Active members not on the team — non-empty only when `canManage`. */
  readonly candidates: readonly AssignableMember[];
}

export interface ProjectTaskListResponse {
  readonly tasks: readonly ProjectTask[];
  readonly total?: number;
  readonly offset?: number;
  readonly limit?: number;
}

export interface OverdueTask {
  readonly ref: string;
  readonly title: string;
  readonly projectRef: string | null;
  readonly statusLabel: string;
  readonly dueDate: string;
  readonly daysOverdue: number;
}

export interface OverdueTaskListResponse {
  readonly tasks: readonly OverdueTask[];
}

export interface Todo {
  readonly ref: string;
  readonly title: string;
  readonly statusLabel: string;
  readonly dueAt: string | null;
}

export interface TodoListResponse {
  readonly todos: readonly Todo[];
}

export interface Reminder {
  readonly ref: string;
  readonly title: string;
  readonly remindAt: string;
  readonly channelLabel: string;
  readonly statusLabel: string;
}

export interface ReminderListResponse {
  readonly reminders: readonly Reminder[];
}

export interface ProjectComment {
  readonly ref: string;
  readonly body: string;
  readonly targetType: 'project' | 'task';
  readonly targetRef: string;
  readonly createdAt: string;
}

export interface CommentListResponse {
  readonly comments: readonly ProjectComment[];
}

/** `GET .../projects/my-work` — apps/api/src/projects/hub.controller.ts `myWork()`. */
export interface MyWorkResponse {
  readonly tasks: readonly ProjectTask[];
  readonly todos: readonly Todo[];
  readonly reminders: readonly Reminder[];
  readonly overdue: {
    readonly projects: readonly OverdueProject[];
    readonly tasks: readonly OverdueTask[];
  };
}

/** `GET .../projects/reports/status` — apps/api/src/projects/reports.controller.ts `status()`. */
export interface StatusCountRow {
  readonly status: string;
  readonly statusLabel: string;
  readonly count: number;
}

export interface StatusCountReport {
  readonly authority: 'operational';
  readonly rows: readonly StatusCountRow[];
  readonly total: number;
}

export interface ProjectsStatusReportResponse {
  readonly authority: 'operational';
  readonly projects: StatusCountReport;
  readonly tasks: StatusCountReport;
}

/** `GET .../projects/reports/overdue` — apps/api/src/projects/reports.controller.ts `overdue()`. */
export interface ProjectsOverdueReportResponse {
  readonly authority: 'operational';
  readonly projects: readonly OverdueProject[];
  readonly tasks: readonly OverdueTask[];
}
